/**
 * Created by neo on 11.01.2026
 * Tracker Connection Section - Manage connected fitness trackers
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useToast } from '../../Hooks/useToast';
import { Theme } from '../../Theme/Theme';
import { TerraProvider, TerraProviderApi } from '../../Model/Terra/TerraProvider';

const SectionContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${Theme.Colors.primary};
  }

  &::placeholder {
    color: ${Theme.Colors.warmGrey};
  }
`;

const ProvidersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ProviderCard = styled.div<{ $disabled?: boolean; $connected?: boolean }>`
  border: 1px solid ${(props) => (props.$connected ? Theme.Colors.success : Theme.Colors.lighterGrey)};
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  background: ${(props) => (props.$connected ? 'rgba(40, 167, 69, 0.05)' : 'white')};
  cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};

  &:hover {
    border-color: ${(props) => (props.$disabled ? Theme.Colors.lighterGrey : Theme.Colors.primary)};
    box-shadow: ${(props) => (props.$disabled ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)')};
  }
`;

const ProviderLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const ProviderName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  text-align: center;
  line-height: 1.2;
`;

const ConnectButton = styled.button<{ $connected?: boolean }>`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.$connected ? Theme.Colors.warmGrey : Theme.Colors.primary)};
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DeviceOnlySection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${Theme.Colors.lighterGrey};
`;

const DeviceOnlyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeviceOnlyDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const AppStoreLinks = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const AppStoreLink = styled.a`
  display: inline-block;
  height: 32px;

  img {
    height: 100%;
    width: auto;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const NoResults = styled.div`
  text-align: center;
  padding: 24px;
  color: ${Theme.Colors.warmGrey};
  font-size: 14px;
`;

export type TrackerConnectionSectionProps = {};

export const TrackerConnectionSection: React.FC<TrackerConnectionSectionProps> = observer(() => {
  const { t } = useTrans();
  const toast = useToast();

  const [providers, setProviders] = useState<TerraProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [search, setSearch] = useState('');

  const sortedProviders = useMemo(() => {
    return [...providers].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [providers]);

  const webProviders = useMemo(() => {
    const web = sortedProviders.filter((p) => p.supportsWeb);
    if (!search.trim()) return web;
    const q = search.trim().toLowerCase();
    return web.filter((p) => p.name.toLowerCase().includes(q));
  }, [sortedProviders, search]);

  const mobileProviders = useMemo(() => {
    const mobile = sortedProviders.filter((p) => !p.supportsWeb);
    if (!search.trim()) return mobile;
    const q = search.trim().toLowerCase();
    return mobile.filter((p) => p.name.toLowerCase().includes(q));
  }, [sortedProviders, search]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const refreshProviders = useCallback(() => {
    return TerraProviderApi.list()
      .then((res) => {
        const connected = res.filter((p) => p.isConnected);
        const notConnected = res.filter((p) => !p.isConnected);
        setProviders([...connected, ...notConnected]);
        return res;
      })
      .catch((err) => {
        console.error('Failed to load providers:', err);
        toast.showToast(
          t('trackers.loadError', { defaultValue: 'Failed to load trackers' }),
          'error',
        );
        return [];
      })
      .finally(() => setLoading(false));
  }, [toast, t]);

  useEffect(() => {
    refreshProviders();
  }, [refreshProviders]);

  // Listen for OAuth callback from popup window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'TERRA_AUTH_CALLBACK') {
        const { result, provider } = event.data;
        if (result === 'success') {
          toast.showToast(
            t('trackers.connectSuccess', { defaultValue: 'Tracker connected successfully!' }),
            'success',
          );
          refreshProviders();
        } else {
          toast.showToast(
            t('trackers.connectError', { defaultValue: 'Failed to connect tracker' }),
            'error',
          );
        }
        setProcessing(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toast, t, refreshProviders]);

  const handleConnect = useCallback(
    async (provider: TerraProvider) => {
      if (!provider.supportsWeb || processing) return;

      setProcessing(true);

      try {
        const response = await provider.connect();

        if (response.auth_url) {
          // Open OAuth in popup window
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          const popup = window.open(
            response.auth_url,
            'TerraOAuth',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`,
          );

          // Monitor popup close
          if (popup) {
            const checkClosed = setInterval(() => {
              if (popup.closed) {
                clearInterval(checkClosed);
                // Refresh providers after popup closes (user may have completed auth)
                setTimeout(() => {
                  refreshProviders();
                  setProcessing(false);
                }, 1000);
              }
            }, 500);
          } else {
            // Popup blocked - fall back to redirect
            window.location.href = response.auth_url;
          }
        } else {
          throw new Error('No auth URL returned');
        }
      } catch (error) {
        console.error('Error connecting tracker:', error);
        toast.showToast(
          t('trackers.connectInitError', { defaultValue: 'Failed to start connection. Please try again.' }),
          'error',
        );
        setProcessing(false);
      }
    },
    [processing, toast, t, refreshProviders],
  );

  const handleDisconnect = useCallback(
    async (provider: TerraProvider) => {
      if (processing) return;

      setProcessing(true);

      try {
        await provider.disconnect();
        toast.showToast(
          t('trackers.disconnectSuccess', { defaultValue: 'Tracker disconnected successfully!' }),
          'success',
        );
        refreshProviders();
      } catch (error) {
        console.error('Error disconnecting tracker:', error);
        toast.showToast(
          t('trackers.disconnectError', { defaultValue: 'Failed to disconnect tracker' }),
          'error',
        );
      } finally {
        setProcessing(false);
      }
    },
    [processing, toast, t, refreshProviders],
  );

  if (loading) {
    return (
      <SectionContainer>
        <SectionTitle>{t('trackers.title', { defaultValue: 'Connected Trackers' })}</SectionTitle>
        <LoadingOverlay>{t('trackers.loading', { defaultValue: 'Loading trackers...' })}</LoadingOverlay>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <SectionTitle>{t('trackers.title', { defaultValue: 'Connected Trackers' })}</SectionTitle>
      <SectionDescription>
        {t('trackers.description', {
          defaultValue: 'Connect your fitness trackers to automatically sync your activities and earn points.',
        })}
      </SectionDescription>

      <SearchInput
        type="text"
        placeholder={t('trackers.searchPlaceholder', { defaultValue: 'Search trackers...' })}
        value={search}
        onChange={handleSearchChange}
      />

      {webProviders.length > 0 ? (
        <ProvidersGrid>
          {webProviders.map((provider) => (
            <ProviderCard key={provider.provider} $disabled={processing} $connected={provider.isConnected}>
              <ProviderLogo src={provider.logoUrl} alt={provider.name} />
              <ProviderName>{provider.name}</ProviderName>
              {provider.isConnected ? (
                <ConnectButton $connected onClick={() => handleDisconnect(provider)} disabled={processing}>
                  {t('trackers.disconnectButton', { defaultValue: 'Disconnect' })}
                </ConnectButton>
              ) : (
                <ConnectButton onClick={() => handleConnect(provider)} disabled={processing}>
                  {t('trackers.connectButton', { defaultValue: 'Connect' })}
                </ConnectButton>
              )}
            </ProviderCard>
          ))}
        </ProvidersGrid>
      ) : (
        <NoResults>{t('trackers.noResults', { defaultValue: 'No trackers found' })}</NoResults>
      )}

      {mobileProviders.length > 0 && (
        <DeviceOnlySection>
          <DeviceOnlyTitle>{t('trackers.deviceOnlyTitle', { defaultValue: 'Device-Specific Trackers' })}</DeviceOnlyTitle>
          <DeviceOnlyDescription>
            {t('trackers.deviceOnlyDescription', {
              defaultValue:
                'The following trackers require the KINASTIC mobile app due to device-specific APIs (Apple Health, Samsung Health, Google Fit).',
            })}
          </DeviceOnlyDescription>
          <AppStoreLinks>
            <AppStoreLink
              href="https://apps.apple.com/app/kinastic-coach/id1465156373"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on App Store"
              />
            </AppStoreLink>
            <AppStoreLink
              href="https://play.google.com/store/apps/details?id=app.flinq.athlete"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Get it on Google Play"
              />
            </AppStoreLink>
          </AppStoreLinks>
          <ProvidersGrid>
            {mobileProviders.map((provider) => (
              <ProviderCard key={provider.provider} $disabled $connected={provider.isConnected}>
                <ProviderLogo src={provider.logoUrl} alt={provider.name} />
                <ProviderName>{provider.name}</ProviderName>
              </ProviderCard>
            ))}
          </ProvidersGrid>
        </DeviceOnlySection>
      )}
    </SectionContainer>
  );
});
